import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API, authFetch } from "@/App";
import { 
  ArrowLeft, FolderKanban, Clock, CheckCircle, Target,
  Calendar, User, FileText, ChevronRight, Building2, Globe, Star
} from "lucide-react";

export const ClientProjectsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    fetchProjects();
    fetchBusinesses();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await authFetch(`${API}/projects`, {  });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
        if (data.length > 0 && !selectedProject) {
          setSelectedProject(data[0]);
        }
      }
    } catch (_error) {
      console.error("Error fetching projects:", _error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const response = await authFetch(`${API}/businesses`, {  });
      if (response.ok) {
        setBusinesses(await response.json());
      }
    } catch (_error) {
      console.error("Error fetching businesses:", _error);
    }
  };

  const getBusinessName = (businessId) => {
    if (!businessId) return "General";
    const business = businesses.find(b => b.business_id === businessId);
    return business?.name || "Unknown";
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { class: "bg-green-100 text-green-700", icon: Clock },
      on_hold: { class: "bg-yellow-100 text-yellow-700", icon: Clock },
      completed: { class: "bg-blue-100 text-blue-700", icon: CheckCircle }
    };
    const s = styles[status] || { class: "bg-gray-100", icon: Clock };
    return (
      <Badge className={s.class}>
        <s.icon className="w-3 h-3 mr-1" />
        {status?.replace("_", " ").toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="client-projects">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-neutral-600">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-neutral-900">My Projects</h1>
              <p className="text-sm text-neutral-500">Track your consulting projects and progress</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Businesses Section */}
        {businesses.length > 0 && (
          <div className="mb-8">
            <h3 className="font-medium text-neutral-700 mb-4">My Businesses</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {businesses.map((business) => (
                <Card key={business.business_id} className={`border-neutral-200 ${business.is_primary ? 'ring-2 ring-emerald-500' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-emerald-700" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-neutral-900">{business.name}</h4>
                          <p className="text-sm text-neutral-500">{business.industry || "No industry"}</p>
                        </div>
                      </div>
                      {business.is_primary && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <Star className="w-3 h-3 mr-1" />
                          Primary
                        </Badge>
                      )}
                    </div>
                    {business.description && (
                      <p className="text-sm text-neutral-600 mt-2 line-clamp-2">{business.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-3 text-sm">
                      <span className="text-neutral-500">
                        <FolderKanban className="w-4 h-4 inline mr-1" />
                        {business.project_count || 0} projects
                      </span>
                      {business.website && (
                        <a href={business.website} target="_blank" rel="noopener noreferrer" 
                           className="flex items-center text-blue-600 hover:text-blue-700">
                          <Globe className="w-4 h-4 mr-1" />
                          Website
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {projects.length === 0 && businesses.length === 0 ? (
          <Card className="border-neutral-200">
            <CardContent className="p-12 text-center">
              <FolderKanban className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-neutral-900 mb-2">No Active Projects</h3>
              <p className="text-neutral-500">
                You don&apos;t have any projects yet. Your consultant will create projects as you work together.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Project List */}
            <div className="space-y-3">
              <h3 className="font-medium text-neutral-700 mb-2">Your Projects</h3>
              {projects.map((project) => (
                <Card 
                  key={project.project_id}
                  className={`border-neutral-200 cursor-pointer transition-all ${
                    selectedProject?.project_id === project.project_id 
                      ? "ring-2 ring-emerald-500 border-emerald-300" 
                      : "hover:border-emerald-200"
                  }`}
                  onClick={() => setSelectedProject(project)}
                  data-testid={`project-item-${project.project_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <Badge variant="outline" className="text-xs">{project.project_type}</Badge>
                      {getStatusBadge(project.status)}
                    </div>
                    <h4 className="font-medium text-neutral-900">{project.title}</h4>
                    <p className="text-xs text-neutral-500 mt-1 flex items-center">
                      <Building2 className="w-3 h-3 mr-1" />
                      {getBusinessName(project.business_id)}
                    </p>
                    <div className="mt-3 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Progress</span>
                        <span className="font-medium">{project.percentage_complete || 0}%</span>
                      </div>
                      <Progress value={project.percentage_complete || 0} className="h-2" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Project Detail */}
            <div className="md:col-span-2">
              {selectedProject ? (
                <Card className="border-neutral-200">
                  <CardHeader className="border-b">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl">{selectedProject.title}</CardTitle>
                        <CardDescription className="mt-1">
                          {selectedProject.project_type}
                        </CardDescription>
                      </div>
                      {getStatusBadge(selectedProject.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Tabs defaultValue="overview">
                      <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0">
                        <TabsTrigger 
                          value="overview" 
                          className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:bg-transparent"
                        >
                          Overview
                        </TabsTrigger>
                        <TabsTrigger 
                          value="updates" 
                          className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:bg-transparent"
                        >
                          Updates ({selectedProject.progress_updates?.length || 0})
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="overview" className="p-6 space-y-6">
                        {/* Progress */}
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-neutral-700">Overall Progress</span>
                            <span className="text-2xl font-bold text-emerald-600">
                              {selectedProject.percentage_complete || 0}%
                            </span>
                          </div>
                          <Progress value={selectedProject.percentage_complete || 0} className="h-3" />
                        </div>

                        {/* Goals */}
                        {selectedProject.goals && (
                          <div>
                            <div className="flex items-center text-neutral-700 font-medium mb-2">
                              <Target className="w-4 h-4 mr-2" />
                              Project Goals
                            </div>
                            <p className="text-neutral-600 bg-neutral-50 p-4 rounded-lg whitespace-pre-wrap">
                              {selectedProject.goals}
                            </p>
                          </div>
                        )}

                        {/* Info */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center text-neutral-600">
                            <Calendar className="w-4 h-4 mr-2 text-neutral-400" />
                            Started: {new Date(selectedProject.created_at).toLocaleDateString()}
                          </div>
                          {selectedProject.consultant && (
                            <div className="flex items-center text-neutral-600">
                              <User className="w-4 h-4 mr-2 text-neutral-400" />
                              Consultant: {selectedProject.consultant.name}
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="updates" className="p-6">
                        {selectedProject.progress_updates?.length > 0 ? (
                          <div className="space-y-4">
                            {[...selectedProject.progress_updates].reverse().map((update, idx) => (
                              <div 
                                key={update.update_id || idx} 
                                className="border-l-4 border-emerald-500 pl-4 py-2"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm text-neutral-500">
                                    {new Date(update.created_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                  <Badge variant="outline" className="text-emerald-700">
                                    {update.percentage_complete}% Complete
                                  </Badge>
                                </div>
                                <p className="text-neutral-700">{update.update_text}</p>
                                
                                {update.tasks_completed?.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-sm font-medium text-green-700 mb-1">✓ Completed:</p>
                                    <ul className="text-sm text-neutral-600 space-y-1">
                                      {update.tasks_completed.map((task, i) => (
                                        <li key={i} className="flex items-center">
                                          <CheckCircle className="w-3 h-3 mr-2 text-green-500" />
                                          {task}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {update.tasks_upcoming?.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-sm font-medium text-blue-700 mb-1">→ Coming Up:</p>
                                    <ul className="text-sm text-neutral-600 space-y-1">
                                      {update.tasks_upcoming.map((task, i) => (
                                        <li key={i} className="flex items-center">
                                          <ChevronRight className="w-3 h-3 mr-2 text-blue-500" />
                                          {task}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {update.blockers && (
                                  <div className="mt-3 p-2 bg-red-50 rounded text-sm text-red-700">
                                    <strong>Blocker:</strong> {update.blockers}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <FileText className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                            <p className="text-neutral-500">No updates yet</p>
                            <p className="text-sm text-neutral-400">
                              Your consultant will post updates as work progresses
                            </p>
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-neutral-200">
                  <CardContent className="p-12 text-center">
                    <p className="text-neutral-500">Select a project to view details</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientProjectsPage;
